<script setup lang="ts">
import { useClaims } from '@auth/useClaims';
import { useWatchErrorHandler } from '@errors/useErrorHandler';
import { useBreadcrumbStore } from '@menu-lib/breadcrumb/breadcrumbStore';
import { adviseurApi } from '@services/crm-internal/service';
import VCollapse from '@shared/component/VCollapse';
import VContentBox from '@shared/component/VContentBox/VContentBox.vue';
import VHeading from '@shared/typography/VHeading/VHeading.vue';
import VSizeContainer from '@shared/layout/VSizeContainer/VSizeContainer.vue';
import { useQuery } from '@tanstack/vue-query';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import VSelect from '@shared/form/VSelect/VSelect.vue';

const route = useRoute();
const breadcrumbStore = useBreadcrumbStore();

const claims = useClaims();
const adviseurnummer = ref<number>();
const titel = ref<string>('Alerts beheren');

watch(
  () => route.query.adviseurnummer,
  (e) => {
    if (!e) return;
    adviseurnummer.value = Number(e);
  },
  { immediate: true, deep: true },
);

const { data: adviseurData, error: adviseurError } = useQuery({
  queryKey: ['adviseurs'],
  queryFn: () => adviseurApi.tussenpersonenGet(),
  select: (result) =>
    result.data.result
      ?.filter((adviseur) => adviseur.id > 0)
      .map((adviseur) => ({ value: adviseur.id, label: `${adviseur.name} (${adviseur.id})` })),
  enabled: computed(() => claims?.value?.isInternal ?? false),
});
useWatchErrorHandler(adviseurError);

breadcrumbStore.breadcrumbs = [{ name: 'Alerts', link: '' }];
</script>

<template>
  <VSizeContainer class="my-4 flex flex-col gap-3" size="md">
    <VCollapse :open="false" label="Interne opties" v-if="claims?.isInternal">
      <VSelect
        searchable
        class="w-full"
        :options="adviseurData"
        option-label="label"
        option-value="value"
        v-model="adviseurnummer"
        placeholder="Selecteer adviseur (interne optie)"
      />
    </VCollapse>

    <VContentBox>
      <VHeading :level="1" appearance="m">{{ titel }}</VHeading>
      <hr />
      <RouterView :key="route.path" v-slot="{ Component }">
        <component
          :is="Component"
          :adviseurnummer="adviseurnummer"
          @titleChanged="(e: string) => (titel = e)"
        />
      </RouterView>
    </VContentBox>
  </VSizeContainer>
</template>
